import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../utils/axiosInstance';

// helper must come AFTER imports to satisfy `import/first`
const isMobile = () =>
  (typeof window !== 'undefined' &&
   window.matchMedia &&
   window.matchMedia('(max-width: 768px)').matches);

const PillList = ({ items }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: (isMobile() ? 'none' : 520), margin: '0 auto' }}>
    {items.map((txt, i) => (
      <div
        key={i}
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile() ? '36px auto' : '36px 1fr',
          gap: 12,
          alignItems: 'center',
        }}
      >
        <div
          style={{
            borderRadius: 10,
            border: '2px solid #0e0e0e',
            background: '#ffffff',
            height: 40,
            width: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            color: '#111',
          }}
        >
          {i + 1}
        </div>
        <div
          className="input"
          style={{
            height: 40,
            display: 'inline-flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            width: 'auto',
            border: '2px solid #0e0e0e',
            borderRadius: 10,
            background: '#fff',
            padding: '0 12px',
            boxShadow: '0 2px 0 #0e0e0e'
          }}
        >
          {txt}
        </div>
      </div>
    ))}
  </div>
);

/** Kendall-style pairwise agreement % over the intersection of items */
function similarityPercent(orderA, orderB) {
  if (!Array.isArray(orderA) || !Array.isArray(orderB)) return 0;
  const posA = new Map(orderA.map((x, i) => [String(x), i]));
  const posB = new Map(orderB.map((x, i) => [String(x), i]));
  const items = orderA.filter((x) => posB.has(String(x)));
  const m = items.length;
  if (m < 2) return 100;
  let concordant = 0;
  let total = 0;
  for (let i = 0; i < m; i++) {
    for (let j = i + 1; j < m; j++) {
      const ai = posA.get(String(items[i]));
      const aj = posA.get(String(items[j]));
      const bi = posB.get(String(items[i]));
      const bj = posB.get(String(items[j]));
      if (ai == null || aj == null || bi == null || bj == null) continue;
      total += 1;
      const sameOrder = (ai - aj) * (bi - bj) > 0;
      if (sameOrder) concordant += 1;
    }
  }
  if (total === 0) return 100;
  return Math.round((concordant / total) * 100);
}

const FriendRankingDetail = () => {
  const { username, listId } = useParams();
  const navigate = useNavigate();
  const [friendListName, setFriendListName] = useState('');
  const [friendOrder, setFriendOrder] = useState([]);
  const [myOrder, setMyOrder] = useState(null); // null = unknown, [] = exists but empty
  const [allowed, setAllowed] = useState(null); // null=loading, true=ok, false=private

  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const f = await axios.get(`/users/${encodeURIComponent(username)}/rankings/${listId}`);
        if (!alive) return;
        setFriendListName(f.data?.list?.name || '');
        setFriendOrder(f.data?.order || []);
        setAllowed(true);

        // try to fetch my own order (best-effort)
        try {
          const me = await axios.get(`/users/me/rankings/${listId}`);
          if (!alive) return;
          setMyOrder(me.data?.order ?? null);
        } catch {
          if (!alive) return;
          setMyOrder(null);
        }
      } catch {
        if (!alive) return;
        // Not friends yet (403) or other failure
        setAllowed(false);
        setFriendListName('');
        setFriendOrder([]);
        setMyOrder(null);
      }
    };
    run();
    return () => { alive = false; };
  }, [username, listId]);

  const bothHave =
    allowed === true &&
    Array.isArray(friendOrder) && friendOrder.length > 0 &&
    Array.isArray(myOrder)   && myOrder.length   > 0;

  const similarity = useMemo(
    () => (bothHave ? similarityPercent(friendOrder, myOrder) : null),
    [bothHave, friendOrder, myOrder]
  );

  // On mobile + comparison, left-align headings; otherwise center.
  const titleAlign = (isMobile() && bothHave) ? 'left' : 'center';

  return (
    <div className="scroll-shell">
      <div className="scroll-body">
        <div style={{ maxWidth: 980, margin: '0 auto' }}>

          {/* Back + Chat grouped together on MOBILE; Back alone on DESKTOP */}
          {isMobile() ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <button
                className="logout-btn back-btn"
                onClick={() => navigate(-1)}
                style={{ marginBottom: 0 }}
              >
                ← Back
              </button>
              <button
                className="btn-outline-black"
                onClick={() => navigate(`/friends/${encodeURIComponent(username)}/chat`)}
                style={{ padding: '8px 14px' }}
              >
                Chat
              </button>
            </div>
          ) : (
            <button
              className="logout-btn back-btn"
              onClick={() => navigate(-1)}
              style={{ marginBottom: 12 }}
            >
              ← Back
            </button>
          )}

          <h2
            style={{
              textAlign: titleAlign,
              fontWeight: 900,
              margin: '10px 0 0'
            }}
          >
            {friendListName ? friendListName : 'List'}
          </h2>

          {allowed === false ? (
            <div style={{ textAlign: 'center', marginTop: 16, color: '#444' }}>
              You can’t view @{username}’s list until you are friends.
            </div>
          ) : (
            <>
              {similarity != null && (
                <div className="similarity"><strong>Similarity: {similarity}%</strong></div>
              )}

              {bothHave ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                  <div>
                    <h4 style={{ textAlign: titleAlign, margin: '0 0 10px' }}>@{username}</h4>
                    <PillList items={friendOrder} />
                  </div>
                  <div>
                    <h4 style={{ textAlign: titleAlign, margin: '0 0 10px' }}>You</h4>
                    <PillList items={myOrder} />
                  </div>
                </div>
              ) : (
                allowed && (
                  <div>
                    <h4 style={{ textAlign: titleAlign, margin: '0 0 10px' }}>@{username}</h4>
                    <PillList items={friendOrder} />
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendRankingDetail;
